import { toggleToast } from '@bilibili-maoer/prelude'

import { ReactComponent as FailureIcon } from '/assets/icons/failure.svg'
import { ReactComponent as SuccessIcon } from '/assets/icons/success.svg'

import { useState } from 'react'
import { useEffectOnce } from 'react-use'

import { Stack, Typography } from '@mui/material'
import { match } from 'ts-pattern'
import { z } from 'zod'

import { useAppDispatch } from '~/app/hooks'
import { ComplexAlert } from '~/core/components/alert'
import { ComplexDialog } from '~/core/components/dialog'
import { GirlSad } from '~/core/components/girl-sad'
import { Title } from '~/core/components/title'
import UiSlice from '~/core/slices/ui'
import { isNot } from '~/core/utils/is'
import { safeParseInt } from '~/core/utils/safe-parse-int'
import { ContractDetails } from '~/features/guild/contract/components/contract-form'
import { ContractInfo } from '~/features/guild/contract/components/contract-info'
import { EContractStatus, EInviteType } from '~/features/guild/contract/enums'
import { InviteDetails } from '~/features/guild/contract/types'
import { formatContractTime } from '~/features/guild/contract/utils'
import { GuildApi } from '~/services/fm/guild'
import CenterCircularProgress from '~/shared/components/center-circular-progress'
import useAppNavigate from '~/shared/navigation/use-app-navigate'
import useAppSearchParam from '~/shared/navigation/use-app-search-params'

const inviteTypeText = (type: EInviteType) =>
  ({
    [EInviteType.Join]: '签约邀请',
    [EInviteType.Renew]: '续约邀请',
  }[type])

/**
 * 签约续约邀请页面，用于处理尚未完成的邀请或者展示已经处理的邀请。
 * 此页面有两个入口：
 * 1. 用户可以通过下发的短信中的链接进入此页面。此时会带有 application_id query 参数。
 * 2. 续约申请页面提示用户存在尚未处理的续约邀请的弹窗中的前往处理按钮会跳转到此页面，并且没有 application_id 参数。没有 application_id
 *    参数时需要查询当前公会最新的续约邀请之进行展示。
 * 如果没有查询到数据，则抛出 NOT FOUND。后渲染页面前需要判断 contract 是否完成处理：
 * 1. 如果 contract 还没有处理，则显示处理表单。
 * 2. 如果 contract 已经处理，则显示结果页面。
 */
export const GuildInvite = () => {
  const dispatch = useAppDispatch()
  const [searchParams] = useAppSearchParam()
  const returnButton = searchParams.get('return_button')
  const applicationId = safeParseInt(searchParams.get('application_id'))
  const ownerId = safeParseInt(searchParams.get('owner_id'))

  useEffectOnce(() => {
    if (returnButton === 'backup') {
      return undefined
    }
    dispatch(UiSlice.actions.setReturnButtonBehavior('Close'))
    return () => {
      dispatch(UiSlice.actions.setReturnButtonBehavior('Backup'))
    }
  })

  if (applicationId) {
    return <GuildInviteByApplicationId applicationId={applicationId} />
  }
  if (ownerId) {
    return <GuildInviteByOwnerId ownerId={ownerId} />
  }
  return <GuildInviteNotFound />
}

const GuildInviteByApplicationId = (props: { applicationId: number }) => {
  const { isLoading, data: details } = GuildApi.getGuildInviteDetails.useQuery({ applyment_id: props.applicationId })
  const [dialog, setDialog] = useState<{ type: 'joined' }>()
  const navigate = useAppNavigate()

  if (isLoading) {
    return <CenterCircularProgress />
  }
  if (!details) {
    return <GuildInviteNotFound />
  }
  return (
    <>
      {details && <Title>公会{inviteTypeText(details.type)}</Title>}

      {details.status === EContractStatus.Unprocessed ? (
        <GuildInviteUnprocessed details={details} onAgreeJoinInvite={() => setDialog({ type: 'joined' })} />
      ) : (
        <GuildInviteProcessed details={details} />
      )}

      <ComplexDialog
        content={
          dialog?.type === 'joined'
            ? `你已成功加入【${details.guild_name}】公会，前往直播中心 - 我的公会可查看合约信息`
            : undefined
        }
        cancel={{
          text: '取消',
          onClick: () => {
            setDialog(undefined)
          },
        }}
        confirm={{
          text: '前往查看',
          onClick: () => {
            navigate('..', { replace: true })
          },
        }}
      />
    </>
  )
}

const GuildInviteByOwnerId = (props: { ownerId: number }) => {
  const { isLoading, data: invites } = GuildApi.getGuildInviteList.useQuery({
    p: 1,
    pagesize: 1,
    user_id: props.ownerId,
  })

  if (isLoading) {
    return <CenterCircularProgress />
  }
  const firstInvite = invites?.items[0]
  if (!firstInvite) {
    return <GuildInviteNotFound />
  }
  return <GuildInviteByApplicationId applicationId={firstInvite.id} />
}

const GuildInviteNotFound = () => (
  <Stack spacing={2} alignItems="center" sx={{ pt: '20rem' }}>
    <GirlSad $width="18.5rem" />
    <Typography variant="subtitle1" color="text.secondary">
      暂无权限查看该邀请
    </Typography>
  </Stack>
)

const GuildInviteUnprocessed = (props: { details: InviteDetails; onAgreeJoinInvite: () => void }) => {
  const [triggerAgreeInvite, agreeInviteResult] = GuildApi.agreeGuildInvite.useMutation()
  const [triggerRejectInvite, rejectInviteResult] = GuildApi.rejectGuildInvite.useMutation()

  return (
    <>
      <ContractDetails
        data={{
          guildId: props.details.guild_id,
          guildName: props.details.guild_name,
          masterId: props.details.user_id,
          duration: props.details.duration,
          rateString: props.details.rate_string,
          settlement: props.details.settlement,
          createTime: props.details.create_time,
          expireTime: props.details.expire_time,
          extendedTime: props.details.contract_expire_time,
          status: props.details.status,
        }}
        source="invite"
        operator={
          (
            {
              [EInviteType.Join]: 'join',
              [EInviteType.Renew]: 'renew',
            } as const
          )[props.details.type]
        }
        isLoading={agreeInviteResult.isLoading || rejectInviteResult.isLoading}
        onSubmit={async () => {
          await triggerAgreeInvite({ applyment_id: props.details.id }).unwrap()
          if (props.details.type === EInviteType.Join) {
            props.onAgreeJoinInvite()
          } else {
            toggleToast('接受成功')
          }
        }}
        onReject={async () => {
          await triggerRejectInvite({ applyment_id: props.details.id }).unwrap()
          toggleToast('拒绝成功')
        }}
      />
    </>
  )
}

const GuildInviteProcessed = (props: { details: InviteDetails }) => (
  <Stack spacing={4}>
    <ComplexAlert
      icon={props.details.status === EContractStatus.Accepted ? SuccessIcon : FailureIcon}
      desc={`处理时间：${formatContractTime(props.details.process_time)}`}
      title={match(props.details.status)
        .with(EContractStatus.Expired, () => `公会状态变更，系统已自动拒绝该${inviteTypeText(props.details.type)}`)
        .with(EContractStatus.Revoked, () => `公会已撤回该${inviteTypeText(props.details.type)}`)
        .with(EContractStatus.Rejected, () => `已拒绝该${inviteTypeText(props.details.type)}`)
        .with(EContractStatus.Accepted, () => `已接受该${inviteTypeText(props.details.type)}`)
        .with(EContractStatus.Outdated, () => `超时未处理，系统已自动拒绝该${inviteTypeText(props.details.type)}`)
        .otherwise(() => '')}
    />
    <ContractInfo
      fields={[
        { label: '公会名称', value: props.details.guild_name },
        { label: '会长M号', value: props.details.user_id.toString() },
        { label: '签约时限', value: props.details.duration },
        props.details.type === EInviteType.Renew && {
          label: '合约延长至',
          value: formatContractTime(props.details.contract_expire_time),
        },
        { label: '最低分成比例', value: props.details.rate_string },
        { label: '结算方式', value: props.details.settlement },
        { label: '邀请时间', value: formatContractTime(props.details.create_time) },
      ].filter(isNot(z.boolean()))}
    />
  </Stack>
)

export const InviteDialog = (props: { content: string | undefined; ownerId: number; onClose: () => void }) => {
  const navigate = useAppNavigate()
  return (
    <ComplexDialog
      content={props.content}
      cancel={{
        text: '取消',
        onClick: props.onClose,
      }}
      confirm={{
        text: '前往处理',
        onClick: () => {
          props.onClose()
          navigate(`/user/guild/invite?return_button=backup&owner_id=${props.ownerId}`)
        },
      }}
    />
  )
}
